import { useState, useMemo } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useUnifiedCourses, useUnifiedCourse, type UnifiedCourse } from '@/lib/hooks/use-unified-courses'
import CourseForm from './CourseForm'
import LessonManagement from './LessonManagement'
import {
  BookOpen, Plus, ArrowLeft, Edit2, Trash2, Eye,
  CheckCircle2, Clock, GraduationCap
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Database } from '@/lib/supabase/supabase-types'

type Course = Database["public"]["Tables"]["courses"]["Row"]
type CourseInsert = Database["public"]["Tables"]["courses"]["Insert"]

// Stat card component
function StatCard({
  icon: Icon,
  count,
  label,
  iconBgClass = 'bg-[rgba(70,105,237,0.10)]'
}: {
  icon: React.ElementType;
  count: number;
  label: string;
  iconBgClass?: string;
}) {
  return (
    <div className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center">
      <div className={cn("w-16 h-16 rounded-full flex items-center justify-center mb-4", iconBgClass)}>
        <Icon className="w-6 h-6 text-[#4669ed]" />
      </div>
      <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
        {count}
      </div>
      <div className="text-sm text-slate-500 mt-2">
        {label}
      </div>
    </div>
  );
}

// Status badge component
function StatusBadge({ isPublished }: { isPublished: boolean }) {
  if (isPublished) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[rgba(16,185,129,0.10)] text-emerald-500">
        <CheckCircle2 className="w-4 h-4" />
        Published
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
      <Clock className="w-4 h-4" />
      Draft
    </span>
  );
}

export default function CourseManagement() {
  const { courses, isLoading, createCourse, updateCourse, deleteCourse, isCreating, isUpdating, isDeleting } = useUnifiedCourses()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingCourse, setEditingCourse] = useState<UnifiedCourse | null>(null)
  const [selectedCourse, setSelectedCourse] = useState<UnifiedCourse | null>(null)

  // Get course detail with lessons when a course is selected
  const { course: selectedCourseDetail } = useUnifiedCourse(selectedCourse?.slug || '')

  // Stats
  const stats = useMemo(() => {
    return {
      total: courses?.length || 0,
      published: courses?.filter(c => c.is_published).length || 0,
      draft: courses?.filter(c => !c.is_published).length || 0,
    };
  }, [courses]);

  const handleCreateCourse = (data: CourseInsert | ({ id: string } & Partial<Course>)) => {
    if (createCourse) {
      createCourse(data as CourseInsert)
      setShowCreateDialog(false)
    }
  }

  const handleUpdateCourse = (data: CourseInsert | ({ id: string } & Partial<Course>)) => {
    if (updateCourse && 'id' in data) {
      updateCourse(data as { id: string } & Partial<Course>)
      setEditingCourse(null)
    }
  }

  const handleDeleteCourse = (courseId: string) => {
    if (confirm('Are you sure you want to delete this course? This will also delete all lessons.')) {
      if (deleteCourse) {
        deleteCourse(courseId)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
        <p className="text-slate-500">Loading courses...</p>
      </div>
    )
  }

  if (selectedCourse) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-[40px] border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSelectedCourse(null)}
                className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Manage Lessons</h2>
                <p className="text-sm text-slate-500">{selectedCourse.title}</p>
              </div>
            </div>
          </div>
        </div>
        <LessonManagement courseId={selectedCourse.id} lessons={selectedCourseDetail?.lessons} />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Stat Cards */}
      <div className="flex gap-6">
        <StatCard icon={BookOpen} count={stats.total} label="Total Courses" />
        <StatCard icon={CheckCircle2} count={stats.published} label="Published" />
        <StatCard icon={Clock} count={stats.draft} label="Draft" />
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-[40px] border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">All Courses</h3>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-bold text-sm"
            style={{
              background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
            }}
          >
            <Plus className="w-5 h-5" />
            Create Course
          </button>
        </div>
      </div>

      {/* Course Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses?.map((course) => (
          <div
            key={course.id}
            className="bg-white rounded-[32px] border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
          >
            {/* Thumbnail */}
            {course.thumbnail_url ? (
              <img
                src={course.thumbnail_url}
                alt={course.title}
                className="w-full h-48 object-cover"
              />
            ) : (
              <div
                className="w-full h-48 flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(70, 105, 237, 0.1) 0%, rgba(54, 84, 233, 0.2) 100%)',
                }}
              >
                <GraduationCap className="w-16 h-16 text-[#4669ed]/40" />
              </div>
            )}

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="text-xl font-semibold text-slate-900">{course.title}</h3>
                <StatusBadge isPublished={course.is_published ?? false} />
              </div>

              {course.description && (
                <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                  {course.description}
                </p>
              )}

              <p className="text-xs text-slate-400 mb-4">
                Slug: /course/{course.slug}
              </p>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCourse(course)}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm"
                  style={{
                    background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                  }}
                >
                  <Eye className="w-4 h-4" />
                  Manage Lessons
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingCourse(course)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border-2 border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCourse(course.id)}
                    disabled={isDeleting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border-2 border-red-200 text-red-600 font-medium text-sm hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {!courses?.length && (
          <div className="col-span-full bg-white rounded-[40px] border border-slate-200 p-12 text-center">
            <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No courses yet</h3>
            <p className="text-slate-500 mb-6">Create your first course to get started.</p>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm"
              style={{
                background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              }}
            >
              <Plus className="w-5 h-5" />
              Create Course
            </button>
          </div>
        )}
      </div>

      {/* Create Course Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl rounded-[24px]">
          <DialogHeader>
            <DialogTitle>Create New Course</DialogTitle>
            <DialogDescription>
              Add a new course to the platform.
            </DialogDescription>
          </DialogHeader>
          <CourseForm
            onSubmit={handleCreateCourse}
            isLoading={isCreating}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Course Dialog */}
      <Dialog open={!!editingCourse} onOpenChange={() => setEditingCourse(null)}>
        <DialogContent className="max-w-2xl rounded-[24px]">
          <DialogHeader>
            <DialogTitle>Edit Course</DialogTitle>
            <DialogDescription>
              Update course information.
            </DialogDescription>
          </DialogHeader>
          <CourseForm
            course={editingCourse}
            onSubmit={handleUpdateCourse}
            isLoading={isUpdating}
            onCancel={() => setEditingCourse(null)}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
